import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, Users, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import GoogleSignInButton from '../components/GoogleSignInButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizerLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuth();
  const navigate = useNavigate();

  // Show Google auth errors
  React.useEffect(() => {
    const err = sessionStorage.getItem('google_auth_error');
    if (err) { toast.error(err); sessionStorage.removeItem('google_auth_error'); }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let response;
      if (isLogin) {
        response = await axios.post(`${API_URL}/api/auth/login/organizer`, { email, password });
      } else {
        response = await axios.post(`${API_URL}/api/auth/register/organizer`, {
          email,
          password,
          first_name: firstName,
          last_name: lastName
        });
      }
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAuth(user, token);
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/organizer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-maroon via-maroon-hover to-maroon" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gold rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-8">
            <Users className="h-10 w-10" />
          </div>
          <h1 className="font-heading text-5xl font-bold mb-4">Organizer Portal</h1>
          <p className="text-xl text-white/90 max-w-md">
            Create and manage your reunion events. Invite attendees, track payments, and make memories.
          </p>
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3 text-white/80">
              <div className="w-8 h-8 rounded-full bg-gold/30 flex items-center justify-center">✓</div>
              <span>Create unlimited events</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <div className="w-8 h-8 rounded-full bg-gold/30 flex items-center justify-center">✓</div>
              <span>Share event codes with attendees</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <div className="w-8 h-8 rounded-full bg-gold/30 flex items-center justify-center">✓</div>
              <span>Track payments & RSVPs</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-cream">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-maroon hover:underline mb-6">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          <Card className="border-0 shadow-xl bg-white" data-testid="organizer-auth-card">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="font-heading text-2xl text-center text-maroon">
                {isLogin ? 'Organizer Sign In' : 'Create Organizer Account'}
              </CardTitle>
              <CardDescription className="text-center">
                {isLogin ? 'Sign in to manage your events' : 'Start organizing your reunion today'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name</Label>
                      <Input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Juan"
                        required={!isLogin}
                        className="bg-cream-paper"
                        data-testid="organizer-firstname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Dela Cruz"
                        required={!isLogin}
                        className="bg-cream-paper"
                        data-testid="organizer-lastname"
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="organizer@example.com"
                      className="pl-10 h-12 bg-cream-paper"
                      required
                      data-testid="organizer-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Password</Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs text-maroon hover:underline"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 bg-cream-paper"
                      required
                      data-testid="organizer-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-maroon hover:bg-maroon-hover text-white rounded-full font-medium text-lg"
                  disabled={loading}
                  data-testid="organizer-submit"
                >
                  {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
                </Button>
              </form>
              
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                <div className="relative flex justify-center"><span className="px-3 bg-white text-sm text-gray-400">or</span></div>
              </div>
              <GoogleSignInButton portal="organizer" />

              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
                  <button
                    onClick={() => setIsLogin(!isLogin)}
                    className="text-maroon font-medium hover:underline"
                    data-testid="toggle-auth-mode"
                  >
                    {isLogin ? 'Register here' : 'Sign in here'}
                  </button>
                </p>
              </div>
              <div className="mt-4 text-center">
                <Link to="/attendee/login" className="text-sm text-gray-500 hover:text-maroon">
                  Are you an attendee? Login here →
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle className="text-maroon">Reset Password</CardTitle>
                  <CardDescription>
                    Enter your email address and we'll send you instructions to reset your password.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="organizer@example.com"
                      className="bg-cream-paper"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowForgotPassword(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-maroon hover:bg-maroon-hover text-white"
                      onClick={async () => {
                        if (!resetEmail) { toast.error('Please enter your email'); return; }
                        try {
                          await axios.post(`${API_URL}/api/auth/forgot-password`, {
                            email: resetEmail,
                            portal: 'organizer'
                          });
                        } catch (e) { /* silent */ }
                        toast.success('If an account exists, password reset instructions have been sent to your email.');
                        setShowForgotPassword(false);
                        setResetEmail('');
                      }}
                    >
                      Send Reset Link
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizerLogin;
